import { Injectable, inject } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { JobDefinitionResponse, JobLogResponse } from '../../../interfaces/response/AdminPageResponses';
import { map } from 'rxjs/operators';
import { JobLogAdapter } from '../../../classes/adapters/response/admin/JobLogAdapter';
import { JobDefinitionAdapter } from '../../../classes/adapters/response/admin/JobDefinitionAdapter';
import { UpdateJobDefinitionRequest } from '../../../interfaces/request/AdminPageRequests';

/**
 * This service communicates with backend to manage all administrative tasks.
 */
@Injectable({
  providedIn: 'root'
})
export class JobsService {
  private http = inject(HttpClient);
  private jobLogAdapter = inject(JobLogAdapter);
  private jobDefinitionAdapter = inject(JobDefinitionAdapter);

  url = environment.apiUrl + '/jobs';

  /**
   * Retrieves logs for the given job.
   * @param jobId identifier of a job.
   */
  getJobLog(jobId: string): Observable<string> {
    const url = `${this.url}/${jobId}/logs`;
    return this.http.get(url).pipe(
      map((item: JobLogResponse) => this.jobLogAdapter.adapt(item)));
  }

  /**
   * Cancels the running job.
   * @param jobId identifier of a job.
   */
  cancelJob(jobId: string): Observable<void> {
    const url = `${this.url}/engine/job/cancel/${jobId}`;
    return this.http.post<void>(url, {});
  }

  /**
   * Retrieves the current definition for the job.
   * @param taskType the type of the task, for which the job definition is retrieved.
   */
  getJobDefinition(taskType: string): Observable<string> {
    const url = `${this.url}/${taskType}/image`;
    return this.http.get(url).pipe(
      map((item: JobDefinitionResponse) => this.jobDefinitionAdapter.adapt(item)));
  }

  /**
   * Updates the job definition for a particular task type.
   * @param taskType the type of task, for which the job definition is updated.
   * @param definition the new job definition.
   */
  changeJobDefinition(taskType: string, definition: string): Observable<void> {
    const url = `${this.url}/${taskType}/image`;
    const request: UpdateJobDefinitionRequest = { jobDefinition: definition };
    return this.http.put<void>(url, request);
  }
}
